import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { TranslationService } from '../../../../Shared/Services/translation.service';
import { HomeFacade } from '../../../../../Facades/home.facade';
import { HOME_AFTERNOON_UNTIL_HOUR, HOME_MORNING_UNTIL_HOUR } from '../../../Consts/home.const';
import { HOME_RING_RADIUS } from '../../../Consts/home-ring.const';

@Component({
  selector: 'app-home-briefing',
  standalone: true,
  imports: [COMMON_IMPORTS],
  templateUrl: './home-briefing.component.html',
  styleUrl: './home-briefing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeBriefingComponent {
  readonly home = inject(HomeFacade);

  private readonly _authService = inject(AuthService);
  private readonly _translationService = inject(TranslationService);

  private readonly _user = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly ringRadius = HOME_RING_RADIUS;
  readonly ringCircumference = 2 * Math.PI * HOME_RING_RADIUS;

  readonly firstName = computed(() => this.resolveFirstName());

  readonly greetingKey = computed(() =>
    this.firstName().length > 0
      ? `app.home.briefing.greeting.${this.timeSlot()}`
      : `app.home.briefing.greeting_anonymous.${this.timeSlot()}`
  );

  readonly today = computed(() => this.formatToday(this._translationService.currentLanguage()));

  readonly postureKey = computed(() => `app.home.briefing.posture.${this.home.posture()}.title`);

  readonly postureDetailKey = computed(() => `app.home.briefing.posture.${this.home.posture()}.detail`);

  readonly postureParams = computed(() => ({
    critical: this.home.criticalCount(),
    total: this.home.signals().length,
  }));

  readonly hasScore = computed(() => this.home.healthScore() !== null);

  readonly scoreLabel = computed(() => {
    const score = this.home.healthScore();
    return score === null ? '' : String(score);
  });

  readonly ringOffset = computed(() => {
    const score = this.home.healthScore() ?? 0;
    return this.ringCircumference * (1 - Math.min(100, Math.max(0, score)) / 100);
  });

  readonly refreshedLabel = computed(() => this.formatClock(this.home.refreshedAt()));

  onRefresh(): void {
    this.home.reloadAccount();
  }

  private timeSlot(): string {
    const hour = new Date().getHours();
    if (hour < HOME_MORNING_UNTIL_HOUR) return 'morning';
    if (hour < HOME_AFTERNOON_UNTIL_HOUR) return 'afternoon';
    return 'evening';
  }

  private resolveFirstName(): string {
    const user = this._user();
    if (user === null) return '';
    return (user.first_name ?? user.displayName ?? user.name ?? '').split(' ')[0] ?? '';
  }

  private formatToday(language: string): string {
    return new Date().toLocaleDateString(language, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }

  private formatClock(moment: Date | null): string {
    if (moment === null) return '';
    return moment.toLocaleTimeString(this._translationService.currentLanguage(), {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
